import React, {useState, useEffect, useMemo} from "react";
import {Autocomplete, TextField, CircularProgress, Typography, Grid} from "@mui/material";
import {PaginationResponse} from "../types/api";
import {debounce} from "lodash";

export interface AdditionalParams {
    [key: string]: any;
}


export interface OptionData {
    id: number | null;
    label: string;
}

interface AutocompleteServerProps {
    fetchOptions: (query: string, page: number, size: number, additionalParams?: AdditionalParams
    ) => Promise<PaginationResponse<OptionData[]>>;
    onScroll?: (event: React.SyntheticEvent) => void;
    onSelect?: (option: OptionData | null) => void;
    onSearch?: (query: string) => void;
    additionalParams?: AdditionalParams;
    size: "small" | "medium" | undefined,
    label: string;
    onBlur: () => void;
    error: boolean;
    value: OptionData | null;
    disabled?: boolean;
}

const PAGE_SIZE = 10;

// Другие импорты и интерфейсы

const AutocompleteServer: React.FC<AutocompleteServerProps> = ({
                                                                   fetchOptions,
                                                                   onScroll,
                                                                   onSelect,
                                                                   onSearch,
                                                                   additionalParams,
                                                                   size = "medium",
                                                                   label,
                                                                   onBlur,
                                                                   error,
                                                                   value,
                                                                   disabled = false,
                                                               }: AutocompleteServerProps) => {
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState<OptionData[]>(!!value ? [value] : []);
    const [loading, setLoading] = useState(false);
    const [showLoading, setShowLoading] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [page, setPage] = useState(0);
    const [totalCount, setTotalCount] = useState(!!value ? 1 : 0);


    // Следим за состоянием loading и устанавливаем задержку отображения индикатора


    // Эффект для запроса данных
    useEffect(() => {
        let active = true;

        if (!open) {
            setOptions([]);
            return;
        }

        setLoading(true);
        setShowLoading(true);
        fetchOptions(searchText, page, PAGE_SIZE, additionalParams)
            .then((response) => {
                if (active) {
                    const {data, totalElements} = response?.data || {
                        data: [{id: null, label: ""}],
                        totalElements: 0
                    };
                    if (!data) {
                        return;
                    }
                    if (page === 0) {
                        setOptions(data);
                    } else {
                        setOptions((prev) => [...prev, ...data!]);
                    }
                    setTotalCount(totalElements!);
                }
            })
            .catch((error) => {
                console.error("Ошибка запроса:", error);
            })
            .finally(() => {
                if (active) {
                    setLoading(false);
                    setTimeout(() => setShowLoading(false), 1_500);
                }
            });

        return () => {
            active = false;
        };
    }, [open, searchText, page]);

    // Обработка изменения текста поиска с debounce
    const debouncedHandleInputChange = useMemo(
        () =>
            debounce((_: React.SyntheticEvent, value: string) => {
                setSearchText(value);
                setPage(0);
                if (onSearch) {
                    onSearch(value);
                }
            }, 500),
        [onSearch]
    );

    // Обработка скролла
    const handleScroll = (event: React.SyntheticEvent) => {
        if (onScroll) {
            onScroll(event);
        }
        const listboxNode = event.currentTarget as HTMLElement;
        if (
            listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight &&
            options.length < totalCount &&
            !loading
        ) {
            setPage((prev) => prev + 1);
        }
    };

    // Обработка выбора опции
    const handleChange = (_: any, value: OptionData | null) => {
        if (onSelect) {
            onSelect(value);
        }
    };

    return (
        <Autocomplete
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            onInputChange={debouncedHandleInputChange}
            onChange={handleChange}
            options={options}
            disabled={disabled}
            loading={loading}
            getOptionLabel={(option) => option.label || ""}
            ListboxProps={{
                onScroll: handleScroll,
            }}
            value={value}
            renderInput={(params) => (
                <TextField
                    {...params}
                    size={size}
                    label={label}
                    onBlur={onBlur}
                    error={error}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {showLoading && <CircularProgress color="inherit" size={20}/>}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
};

export default AutocompleteServer;